import { useEffect, useId, useRef, useState } from 'react';
import {
  PRODUCT_ATTRIBUTE_BARCODE_HINT,
  normalizeProductBarcode,
  productAttributeBarcodeError,
} from '@/utils/productBarcode';
import type { BarcodeOwner } from '@/infrastructure/ProductAttributesClient';
import { INTERNAL_BARCODE_UNAVAILABLE } from '@/utils/internalBarcode';
import { asciiScanField } from './asciiScanField';
import { usePromptDialog } from './usePromptDialog';
import { BarcodeOwnerCard } from './BarcodeOwnerCard';

export interface BarcodeCellProps {
  sku: string;
  /** From the catalog, or from a save this session; absent means the product has never had one. */
  barcode: string | undefined;
  productId: number | undefined;
  /** Answers the product that already carries the code when the save is refused for that (SSD-7532). */
  onSave: (productId: number, barcode: string) => Promise<BarcodeOwner | void>;
  /** A locked order's table: the box shows but takes nothing. */
  disabled?: boolean;
  /** A known barcode can be replaced too, for a catalog code that does not match the packaging (SSD-7541). */
  editable?: boolean;
  /** Called once an edit closes with the cursor left nowhere, so a scan desk can take it back. */
  onEditClosed?: () => void;
  /** Names the product in the confirmation a replacement asks for; the sku stands in without it. */
  productName?: string | undefined;
  /**
   * Gives a product with no barcode an internal code of its own (SSD-7611): `code` is the one its SKU makes, null
   * when the SKU cannot make one, and `onMake` saves it (and prints its sticker once saved). A product that already
   * carries its internal code can print the sticker again with `onPrint`. Without it the cell offers neither button.
   */
  makeCode?: MakeCode;
}

export interface MakeCode {
  code: string | null;
  onMake: (productId: number, code: string) => Promise<BarcodeOwner | void>;
  /** Prints the sticker again: a print that failed or a sticker that came off leaves the code with no label otherwise. */
  onPrint: (code: string) => Promise<void>;
}

/**
 * The barcode column. A product the catalog already knows reads as a ticked number; one it does not
 * carries an always-visible input, because the value typed here is what makes the next scan resolve.
 * Saved against the product master, not the order — the same gap follows the sku onto every order.
 * Where `editable`, a click on a known code opens the same input to scan the packaging's code over it; the
 * replacement asks first, since the old code then stops resolving on every order for the sku.
 */
export function BarcodeCell({
  sku,
  barcode,
  productId,
  onSave,
  disabled = false,
  editable = false,
  onEditClosed,
  productName,
  makeCode,
}: BarcodeCellProps) {
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [refused, setRefused] = useState<{ barcode: string; owner: BarcodeOwner } | null>(null);
  const [editing, setEditing] = useState(false);
  const [printing, setPrinting] = useState(false);
  // A save that went through hands back the new code, which is what closes the edit; a refusal leaves it open.
  const [shownBarcode, setShownBarcode] = useState(barcode);
  const errorId = useId();
  // What the product master would refuse the code for, in its words; an empty box says nothing (SSD-7996).
  const draftError = draft === '' ? null : productAttributeBarcodeError(draft);
  const invalid = draftError !== null;
  const inputRef = useRef<HTMLInputElement>(null);
  const { askConfirm, dialog } = usePromptDialog();
  // The code on screen when a confirmation is answered, which may not be the one it asked about.
  const currentBarcode = useRef(barcode);
  currentBarcode.current = barcode;

  // The box is disabled while a save is out, which drops the cursor to the page; a refusal leaves the box up,
  // so it takes the cursor back for the rescan. A field the operator moved to meanwhile keeps it.
  const wasSaving = useRef(false);
  useEffect(() => {
    const active = document.activeElement;
    if (wasSaving.current && !saving && inputRef.current && (active === null || active === document.body)) {
      inputRef.current.focus();
    }
    wasSaving.current = saving;
  }, [saving]);

  // The edit's input leaves the page as it closes, and a cursor left nowhere would drop the next scan. A field
  // the operator moved to while the save was out (the Shelf) keeps the cursor.
  const wasEditing = useRef(false);
  useEffect(() => {
    const active = document.activeElement;
    if (wasEditing.current && !editing && (active === null || active === document.body)) {
      onEditClosed?.();
    }
    wasEditing.current = editing;
  }, [editing, onEditClosed]);

  if (barcode !== shownBarcode) {
    setShownBarcode(barcode);
    setEditing(false);
    setDraft('');
    setRefused(null);
  }

  const cancelEdit = () => {
    setEditing(false);
    setDraft('');
    setRefused(null);
  };

  if (barcode && !editing) {
    // The product's own internal code can print its sticker again, on a line of its own under the code (SSD-7611).
    const reprint =
      makeCode && makeCode.code === barcode ? (
        <button
          type="button"
          className="btn btn-line btn-sm barcode-reprint"
          aria-label={`Print the label for ${barcode}`}
          title="Prints this product's code label again"
          disabled={disabled || printing}
          onClick={() => {
            setPrinting(true);
            void makeCode.onPrint(barcode).finally(() => setPrinting(false));
          }}
        >
          Print label
        </button>
      ) : null;
    // The code itself is the control: the column is sized to the check and 13 digits, with no room for a button.
    if (editable && !disabled && productId != null) {
      // A confirmation still open when the code changed stays up to be answered, rather than stranded unseen.
      return (
        <>
          <button
            type="button"
            className="barcode-known barcode-edit num"
            aria-label={`Change barcode ${barcode} for ${sku}`}
            title="Change barcode"
            onClick={() => setEditing(true)}
          >
            <span aria-hidden="true">✓</span>
            <span>{barcode}</span>
          </button>
          {reprint}
          {dialog}
        </>
      );
    }
    return (
      <>
        <span className="barcode-known num">
          <span aria-hidden="true">✓</span>
          <span>{barcode}</span>
        </span>
        {reprint}
        {dialog}
      </>
    );
  }

  const save = async () => {
    if (disabled || productId == null || draft === '' || invalid || saving) {
      return;
    }
    // Saving the code the product already has changes nothing, so it only closes the edit.
    if (draft === barcode) {
      cancelEdit();
      return;
    }
    // Replacing a code is a product-master write every order for the sku reads, so it is confirmed first;
    // backing out keeps the old code and saves nothing.
    if (editing && barcode) {
      const agreed = await askConfirm({
        title: 'Replace barcode?',
        message: `${productName ?? sku}: ${barcode} → ${draft}`,
        confirmLabel: 'Replace',
      });
      // A catalog read that changed the code meanwhile has already closed the edit; the answer was about the old one.
      if (!agreed || currentBarcode.current !== barcode) {
        cancelEdit();
        return;
      }
    }
    setSaving(true);
    try {
      const owner = await onSave(productId, draft);
      setRefused(owner ? { barcode: draft, owner } : null);
    } finally {
      setSaving(false);
    }
  };

  const make = async () => {
    if (!makeCode?.code || disabled || productId == null || saving) {
      return;
    }
    setDraft('');
    setSaving(true);
    try {
      const owner = await makeCode.onMake(productId, makeCode.code);
      setRefused(owner ? { barcode: makeCode.code, owner } : null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="barcode-entry">
      <input
        ref={inputRef}
        className="bcin num"
        aria-label={editing ? `New barcode for ${sku}` : `Barcode for ${sku}`}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? errorId : undefined}
        // The code being replaced stays in sight until the new one is scanned over it. The rule for a new code is cut off
        // in Inventory's narrow barcode box, so the box says what to do and the rule is the tooltip (SSD-7674).
        placeholder={editing ? barcode : 'Scan or type'}
        title={PRODUCT_ATTRIBUTE_BARCODE_HINT}
        value={draft}
        disabled={saving || disabled}
        autoFocus={editing}
        // Korean-mode Hangul is mapped back to the keys it came from, so a scan read as letters is refused for what it is.
        {...asciiScanField(
          (value) => {
            setDraft(normalizeProductBarcode(value));
            // A new code is a new attempt; the card was about the one refused.
            setRefused(null);
          },
          (event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void save();
            } else if (event.key === 'Escape' && editing) {
              event.preventDefault();
              cancelEdit();
            }
          },
        )}
      />
      <button
        type="button"
        className="btn btn-green btn-sm"
        aria-label={`Save barcode for ${sku}`}
        disabled={saving || disabled || productId == null || draft === '' || invalid}
        onClick={() => void save()}
      >
        ✓
      </button>
      {editing && (
        <button
          type="button"
          className="btn btn-line btn-sm"
          aria-label={`Keep barcode ${barcode} for ${sku}`}
          disabled={saving}
          onClick={cancelEdit}
        >
          ✕
        </button>
      )}
      {/* A product with no code of its own gets one, on its own line so the column keeps its width (SSD-7611). */}
      {makeCode && !editing && (
        <button
          type="button"
          className="btn btn-line btn-sm barcode-make"
          aria-label={`Make a barcode for ${sku}`}
          title={
            !makeCode.code
              ? INTERNAL_BARCODE_UNAVAILABLE
              : productId == null
                ? 'This product is not in the catalog yet'
                : `Saves ${makeCode.code} as this product's barcode and prints its label`
          }
          disabled={saving || disabled || productId == null || !makeCode.code}
          onClick={() => void make()}
        >
          Make code
        </button>
      )}
      {invalid && (
        <div id={errorId} className="text-danger" role="alert">
          {draftError}
        </div>
      )}
      {refused && <BarcodeOwnerCard barcode={refused.barcode} owner={refused.owner} />}
      {dialog}
    </div>
  );
}
